"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import {
  FaXTwitter,
  FaLinkedin,
  FaYoutube,
  FaInstagram,
  FaTiktok,
  FaFacebookF,
} from "react-icons/fa6";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useRouter } from "next/navigation";

const Footer = () => {
  const router = useRouter(); // ✅ Utilise `useRouter` pour la redirection
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Ouvrir / fermer la barre de recherche
  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    setSearchQuery(""); // Réinitialiser la recherche
  };

  // ✅ Gérer l'envoi de la recherche
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault(); // Empêcher le rechargement de la page
    if (searchQuery.trim() !== "") {
      router.push(`/search?query=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false); // Fermer la barre après recherche
    }
  };

  return (
    <footer className="mt-10 bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white">
      <div className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5">
          {/* Logo et description */}
          <div>
            <Link href="/" className="flex items-center">
              <Image
                src="/logo-v2.jpeg"
                alt="Logo"
                width={40}
                height={40}
                className="h-10 w-10"
              />
              <span className="ml-2 text-2xl font-semibold">Les Concernés</span>
            </Link>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Votre magazine d'analyse
            </p>
          </div>

          {/* Liens principaux */}
          <FooterColumn title="Navigation">
            <FooterLink href="/" label="Accueil" />
            <FooterLink href="/category/a-la-une" label="À la Une" />
            <FooterLink
              href="/category/videos-podcasts"
              label="Vidéos & Podcasts"
            />
            <FooterLink href="/category/afrique" label="Afrique" />
            <FooterLink href="/category/international" label="International" />
            <FooterLink href="/about" label="À propos" />
          </FooterColumn>

          {/* Décryptage */}
          <FooterColumn title="Décryptage">
            <FooterLink href="/category/economie" label="Économie" />
            <FooterLink href="/category/politique" label="Politique" />
            <FooterLink href="/category/culture" label="Culture" />
            <FooterLink href="/category/sport" label="Sport" />
          </FooterColumn>

          {/* Analyse */}
          <FooterColumn title="Analyse">
            <FooterLink href="/category/essais" label="Essais" />
            <FooterLink href="/category/entretien" label="Entretiens" />
            <FooterLink href="/category/opinion" label="Opinions" />
            <FooterLink href="/category/nouvelle" label="Nouvelle" />
          </FooterColumn>

          {/* Autre */}
          <FooterColumn title="Autres">
            <FooterLink href="/contact" label="Contact" />
            <FooterLink href="/don" label="Faire un don" />
            <FooterLink href="/adhesion" label="Adhérer à l'association" />
            <FooterLink href="/contribuer" label="Contribuer" />
            <FooterLink href="/privacy" label="Confidentialité" />
          </FooterColumn>
        </div>

        {/* Barre de recherche */}
        <div className="mt-10 text-center">
          {!isSearchOpen && (
            <button
              onClick={toggleSearch}
              className="text-gray-700 hover:text-blue-500 dark:text-white"
              aria-label="Ouvrir la recherche"
            >
              <FaSearch size={20} />
            </button>
          )}

          {isSearchOpen && (
            <form onSubmit={handleSubmit} className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 rounded-md bg-gray-100 px-4 py-2 text-gray-900 transition-all focus:outline-none focus:ring focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                autoFocus
                onKeyDown={(e) => e.key === "Escape" && toggleSearch()} // Fermer avec Escape
              />

              {/* ❌ Bouton pour fermer la recherche */}
              <button
                type="button"
                onClick={toggleSearch}
                className="absolute right-2 top-2 text-gray-500 hover:text-red-500"
              >
                <FaTimes size={16} />
              </button>
            </form>
          )}
        </div>

        {/* Réseaux sociaux */}
        <div className="mt-10 text-center">
          <h3 className="mb-4 text-lg font-semibold">Suivez-nous</h3>
          <div className="flex justify-center space-x-4">
            <SocialIcon
              href="https://x.com/maglesconcernes?t=rERA2tJwLVRQktgypfQq4Q"
              color="text-black hover:text-gray-700"
            >
              <FaXTwitter />
            </SocialIcon>
            <SocialIcon
              href="https://www.linkedin.com/company/les-conc%C3%A9rnes/"
              color="text-blue-600 hover:text-blue-700"
            >
              <FaLinkedin />
            </SocialIcon>
            <SocialIcon
              href="https://www.youtube.com/@lesconcernes"
              color="text-red-600 hover:text-red-700"
            >
              <FaYoutube />
            </SocialIcon>
            <SocialIcon
              href="https://www.instagram.com/les_concernes/profilecard/?igsh=Y3drdzlhazh2bTB5"
              color="text-pink-600 hover:text-pink-700"
            >
              <FaInstagram />
            </SocialIcon>
            <SocialIcon
              href="https://tiktok.com"
              color="text-black hover:text-gray-700"
            >
              <FaTiktok />
            </SocialIcon>
            <SocialIcon
              href="https://www.facebook.com/profile.php?id=100092728413759"
              color="text-blue-500 hover:text-blue-700"
            >
              <FaFacebookF />
            </SocialIcon>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-10 border-t border-gray-200 pt-5 text-center dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            © {new Date().getFullYear()} Les Concernés. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

/* 🔹 Composant pour une colonne du footer */
const FooterColumn = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div>
    <h3 className="mb-4 text-lg font-semibold">{title}</h3>
    <ul className="space-y-2">{children}</ul>
  </div>
);

/* 🔹 Composant pour les liens du footer */
const FooterLink = ({ href, label }: { href: string; label: string }) => (
  <li>
    <Link
      href={href}
      className="text-gray-700 hover:text-blue-500 hover:underline dark:text-gray-300 dark:hover:text-blue-400"
    >
      {label}
    </Link>
  </li>
);

/* 🔹 Composant Icônes sociales */
const SocialIcon = ({
  href,
  color,
  children,
}: {
  href: string;
  color: string;
  children: React.ReactNode;
}) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`${color} text-2xl transition`}
  >
    {children}
  </Link>
);

export default Footer;
